import { ColorEnum } from '@/constants/color'
import IconButton from '@/features/ui/buttons/components/IconButton'
import BackArrowIcon from '@/features/ui/icons/components/BackArrowIcon'
import CloseIcon from '@/features/ui/icons/components/CloseIcon'
import SearchIcon from '@/features/ui/icons/components/SearchIcon'
import InputField from '@/features/ui/inputs/components/InputField'
import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { Pressable, View } from 'react-native'
import { createStyleSheet } from 'react-native-unistyles'

interface CatalogHeaderProps {
    searchOpen: boolean
    searchValue: string
    onSearchOpen: () => void
    onSearchClose: () => void
    onSearchChange: (value: string) => void
}

const CatalogHeader: FC<CatalogHeaderProps> = ({
    searchOpen,
    searchValue,
    onSearchOpen,
    onSearchClose,
    onSearchChange,
}) => {
    const { t } = useTranslation('app')

    if (searchOpen) {
        return (
            <View style={styles.container}>
                <Pressable
                    accessibilityLabel={t('actions.back')}
                    accessibilityRole="button"
                    onPress={onSearchClose}
                    style={styles.backButton}
                >
                    <BackArrowIcon color={ColorEnum.GRAY_900} />
                </Pressable>
                <InputField
                    accessibilityLabel={t('openTabs.catalog.searchAccessibilityLabel')}
                    autoCapitalize="none"
                    autoCorrect={false}
                    autoFocus
                    placeholder={t('pos.searchProduct')}
                    value={searchValue}
                    onChange={onSearchChange}
                    FrontComponent={<SearchIcon fill={ColorEnum.GRAY_300} height={16} width={16} />}
                    TailComponent={
                        searchValue ? (
                            <IconButton
                                Icon={CloseIcon}
                                accessibilityLabel={t(
                                    'openTabs.catalog.clearSearchAccessibilityLabel'
                                )}
                                accessibilityRole="button"
                                mode="rounded"
                                size={24}
                                iconSize={16}
                                onPress={() => onSearchChange('')}
                            />
                        ) : undefined
                    }
                    inputStyle={styles.searchInput}
                    style={styles.searchField}
                />
            </View>
        )
    }

    return (
        <View style={styles.container}>
            <Pressable
                accessibilityLabel={t('openTabs.catalog.searchAccessibilityLabel')}
                accessibilityRole="button"
                hitSlop={8}
                onPress={onSearchOpen}
                style={({ pressed }) => [styles.searchButton, pressed && styles.pressed]}
            >
                <SearchIcon fill={ColorEnum.BRICK_700} height={24} width={24} />
            </Pressable>
        </View>
    )
}

const styles = createStyleSheet({
    container: {
        height: 64,
        flexDirection: 'row',
        alignItems: 'center',
        paddingLeft: 16,
        paddingRight: 20,
        borderBottomWidth: 1,
        borderBottomColor: ColorEnum.GRAY_200,
        backgroundColor: ColorEnum.WHITE,
    },
    searchButton: {
        marginLeft: 'auto',
        width: 32,
        height: 32,
        alignItems: 'center',
        justifyContent: 'center',
    },
    backButton: {
        width: 32,
        height: 32,
        alignItems: 'center',
        justifyContent: 'center',
        marginRight: 8,
    },
    searchField: {
        flex: 1,
        minWidth: 0,
        paddingHorizontal: 16,
    },
    searchInput: {
        paddingHorizontal: 0,
    },
    pressed: {
        opacity: 0.64,
    },
})

export default CatalogHeader
